import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/context/AuthProvider';
import { usePayment } from '@/context/payment/PaymentProvider';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, CheckCircle, CreditCard } from 'lucide-react';
import { processStripePayment } from '@/utils/stripePaymentProcessor';

const stripePaymentSchema = z.object({
  recipientEmail: z.string().email('Valid email required'),
  amount: z.string().refine(val => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
    message: 'Amount must be greater than 0',
  }),
  cardNumber: z.string().regex(/^\d{16}$/, 'Card number must be 16 digits'),
  cardholderName: z.string().min(3, 'Name is required'),
  expiryDate: z.string().regex(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, 'Format must be MM/YY'),
  cvv: z.string().regex(/^\d{3,4}$/, 'CVV must be 3-4 digits'),
});

type StripePaymentFormValues = z.infer<typeof stripePaymentSchema>;

export default function StripeDirectPayment() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { user } = useAuth();
  const { addTransaction } = usePayment();
  
  const form = useForm<StripePaymentFormValues>({
    resolver: zodResolver(stripePaymentSchema),
    defaultValues: {
      recipientEmail: '',
      amount: '1.00',
      cardNumber: '',
      cardholderName: '',
      expiryDate: '',
      cvv: '',
    },
  });

  const onSubmit = async (data: StripePaymentFormValues) => {
    if (!user?.email) {
      toast.error('You must be signed in to make payments');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const senderName = user?.username || user?.email || 'Anonymous User';
      
      const result = await processStripePayment({
        amount: parseFloat(data.amount),
        recipientEmail: data.recipientEmail,
        senderEmail: user.email,
        senderName: senderName,
        cardDetails: {
          cardNumber: data.cardNumber,
          cardholderName: data.cardholderName,
          expiryDate: data.expiryDate,
          cvv: data.cvv,
        }
      });
      
      if (result.success) {
        setIsComplete(true);
        toast.success('Payment processed successfully', {
          description: `$${data.amount} sent to ${data.recipientEmail}`
        });
        
        // Add to transaction history
        if (addTransaction) {
          addTransaction({
            amount: parseFloat(data.amount),
            fee: parseFloat(data.amount) * 0.029 + 0.30, // Standard Stripe fee
            recipient: data.recipientEmail,
            recipientEmail: data.recipientEmail,
            sender: senderName,
            senderEmail: user.email,
            status: 'completed',
            method: 'stripe',
          });
        }
        
        // Reset form after 1 second
        setTimeout(() => {
          form.reset();
          setIsComplete(false);
          setIsProcessing(false);
        }, 1000);
      } else {
        toast.error('Payment failed', {
          description: result.error || 'An error occurred while processing your payment'
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment error', {
        description: 'An unexpected error occurred'
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">Pay with Stripe</h2>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="recipientEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient Email</FormLabel>
                  <FormControl>
                    <Input placeholder="recipient@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (USD)</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                      <Input className="pl-7" placeholder="1.00" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="rounded-md border border-gray-200 p-4">
            <h3 className="mb-3 font-medium">Card Details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="cardNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Card Number</FormLabel>
                    <FormControl>
                      <Input placeholder="4242424242424242" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cardholderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cardholder Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Smith" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Date</FormLabel>
                    <FormControl>
                      <Input placeholder="MM/YY" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cvv"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CVV</FormLabel>
                    <FormControl>
                      <Input placeholder="123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="mt-6">
            <Button 
              type="submit" 
              className="w-full"
              disabled={isProcessing || isComplete}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : isComplete ? (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Payment Complete
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay with Stripe
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>

      <div className="mt-4 text-center text-xs text-gray-500">
        <p>Secured by Stripe. Your card details are never stored on our servers.</p>
        <p className="mt-1">Test card: 4242 4242 4242 4242, any future date, any 3 digits</p>
      </div>
    </div>
  );
}
